import {gql} from '@apollo/client';

import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

export const PROCESS_TASK_FILE_STATE_QUERY = gql`
  query ProcessTaskFileStateQuery($taskId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTask(id: $taskId) {
      ... on ProcessTaskNode {
        id
        name
        url
        schema {
          id
          name
          executionType
          outputSchema {
            id
          }
        }
        rootTasks {
          id
          executionFlowchart {
            id
          }
        }
        executionFlowchart {
          id
        }
        procedureStepExecutionInstance {
          ... on ProcedureStepExecutionInstanceNode {
            id
            methodExecutionInstance {
              id
              templateMethodVersion {
                ... on ProcedureMethodDefinitionVersionNode {
                  id
                  name
                }
                ... on InaccessibleInfoNode {
                  ...InaccessibleInfoGraphQL
                }
              }
              methodTask {
                ... on ProcessTaskNode {
                  id
                  rootTasks {
                    id
                    executionFlowchart {
                      id
                    }
                  }
                }
                ...InaccessibleInfoGraphQL
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...HasArchiveRecordGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROCESS_TASK_OUTPUTS_QUERY = gql`
  query ProcessTaskOutputsQuery($taskId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTask(id: $taskId) {
      ... on ProcessTaskNode {
        id
        name
        process {
          id
        }
        status {
          ... on ProcessTaskStatusNode {
            id
          }
        }
        schema {
          id
          name
          prefix
          allFields
          fields
          executionType
          clientOrigin
          entryTemplate {
            id
            name
          }
          canSetAssigneeOnTaskCreation
          taskGroupInstructions
          archiveRecord {
            purpose
          }
          outputSchema {
            id
            name
            prefix
            allFields
            fields
            processExecutionType
            taskFinderLookupConfig {
              id
            }
            archiveRecord {
              purpose
            }
          }
          statusLifecycle {
            ... on ProcessTaskStatusLifecycleNode {
              ...ProcessTaskStatusLifecycleGraphQL
            }
          }
        }
        outputs {
          ... on ProcessOutputNode {
            ...ProcessOutputGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('ProcessOutput').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROCESS_TASK_DETAILS_QUERY = gql`
  # processTask.lineageEdgesTo does not query id
  # eslint-disable benchlint/id-field-required
  query ProcessTaskDetailsQuery($processTaskId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTask(id: $processTaskId) {
      ... on ProcessTaskNode {
        id
        name
        humanId
        status {
          ... on ProcessTaskStatusNode {
            id
            statusType
            displayName
          }
        }
        lastStatusChange {
          id
          createdAt
          creator {
            id
            name
            handle
            avatarUrl
          }
        }
        assignee {
          ... on UserNode {
            id
            name
            handle
            avatarUrl
          }
        }
        scheduledOnDate
        process {
          id
          name
          editUrl
          requestV2Submission {
            ... on RequestV2SubmissionNode {
              id
            }
            ...InaccessibleInfoGraphQL
          }
        }
        schema {
          id
          name
          fields
          allFields
          executionType
          clientOrigin
          entryTemplate {
            id
            name
            currentPublishingRecord {
              ... on PublishingRecordNode {
                id
                publishingStatus
              }
            }
          }
          statusLifecycle {
            ... on ProcessTaskStatusLifecycleNode {
              ...ProcessTaskStatusLifecycleGraphQL
            }
          }
          canSetAssigneeOnTaskCreation
          defaultEntryExecutionFolderOrInaccessible {
            ... on FolderNode {
              id
            }
            ...InaccessibleInfoGraphQL
          }
          outputSchema {
            id
          }
        }
        executionOrigin {
          createdAt
          executionType
          executionEntry {
            id
          }
        }
        modifiedAt
        createdAt
        creator {
          id
          name
          handle
          avatarUrl
        }
        executor {
          id
          name
          handle
          avatarUrl
        }
        creationOrigin {
          originType
        }
        lineageEdgesTo {
          fromVertex {
            ... on ProcessTaskNode {
              id
              name
            }
            ... on ProcessOutputNode {
              id
              name
            }
          }
        }
        rootTasks {
          id
          name
        }
        processAsyncRecords {
          ... on ProcessAsyncRecordNode {
            id
            status
          }
        }
        fields
        responsibleParties {
          ... on PartyInterface {
            id
            name
            handle
            avatarUrl
          }
        }
        permissions {
          ...BasePermissionsGraphQL
        }
        sortValue
      }
      ...HasArchiveRecordGraphQL
      ...InaccessibleInfoGraphQL
      ...ProcessTaskProcedureInfoGraphQL
      ...ProcessTaskPipelineInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcessTaskProcedureInfo').fragment}
  ${getFragmentWrapper('ProcessTaskPipelineInfo').fragment}
  ${getFragmentWrapper('BasePermissions').fragment}
`;
